import { useCallback, useRef } from "react"
import { useQueryClient } from "@tanstack/react-query"
import { toast } from "sonner"
import { useAiStore } from "@/stores/ai-store"
import { SseParser } from "@/lib/sse-parser"
import type { MentionEntity } from "@/lib/ag-ui-types"

function getCsrfToken(): string | null {
  const meta = document.querySelector('meta[name="csrf-token"]')
  return meta?.getAttribute("content") ?? null
}

interface RunParams {
  conversationId?: string | null
  content: string
  currentPage?: string
  contextType?: string
  contextLabel?: string
  mentionedEntities?: MentionEntity[]
}

export function useAgUiStream() {
  const queryClient = useQueryClient()
  const abortRef = useRef<AbortController | null>(null)
  const {
    setStreaming,
    appendStreamContent,
    resetStreamContent,
    addRichToolCall,
    appendToolArgs,
    completeRichToolCall,
    startStep,
    finishStep,
    setActiveConversation,
  } = useAiStore()

  const startRun = useCallback(
    async (params: RunParams) => {
      // Abort any existing stream
      abortRef.current?.abort()
      const controller = new AbortController()
      abortRef.current = controller

      resetStreamContent()
      setStreaming(true)

      const body: Record<string, unknown> = { content: params.content }
      if (params.conversationId) body.conversation_id = params.conversationId
      if (params.currentPage) body.current_page = params.currentPage
      if (params.contextType) body.context_type = params.contextType
      if (params.contextLabel) body.context_label = params.contextLabel
      if (params.mentionedEntities?.length) {
        body.mentioned_entities = params.mentionedEntities.map((e) => ({
          id: e.id,
          type: e.type,
          title: e.title,
        }))
      }

      const headers: Record<string, string> = {
        "Content-Type": "application/json",
        Accept: "text/event-stream",
      }
      const csrfToken = getCsrfToken()
      if (csrfToken) headers["X-CSRF-Token"] = csrfToken

      try {
        const response = await fetch("/api/v1/ai/run", {
          method: "POST",
          headers,
          body: JSON.stringify(body),
          credentials: "same-origin",
          signal: controller.signal,
        })

        if (!response.ok) {
          setStreaming(false)
          const body = await response.json().catch(() => ({}))
          const msg = body.error || `Błąd serwera (${response.status})`
          toast.error(msg)
          return
        }

        const reader = response.body?.getReader()
        if (!reader) {
          setStreaming(false)
          return
        }

        const decoder = new TextDecoder()
        const parser = new SseParser()
        let conversationId = params.conversationId

        while (true) {
          const { done, value } = await reader.read()
          if (done) break

          const chunk = decoder.decode(value, { stream: true })
          const events = parser.feed(chunk)

          for (const event of events) {
            const d = event.data as Record<string, unknown>

            switch (event.type) {
              case "RUN_STARTED":
                // Extract conversation_id if present
                if (d.conversationId && !conversationId) {
                  conversationId = d.conversationId as string
                  setActiveConversation(conversationId)
                }
                break

              case "TEXT_MESSAGE_CONTENT":
                appendStreamContent((d.delta as string) || "")
                break

              case "TOOL_CALL_START":
                addRichToolCall(d.toolCallId as string, d.toolCallName as string)
                break

              case "TOOL_CALL_ARGS":
                appendToolArgs(d.toolCallId as string, (d.delta as string) || "")
                break

              case "TOOL_CALL_RESULT":
                completeRichToolCall(d.toolCallId as string, (d.result as string) || "")
                break

              case "STEP_STARTED":
                startStep(d.stepId as string, (d.name as string) || "")
                break

              case "STEP_FINISHED":
                finishStep(d.stepId as string)
                break

              case "RUN_FINISHED":
                setStreaming(false)
                // Invalidate queries so UI refreshes with persisted messages
                if (conversationId) {
                  queryClient.invalidateQueries({ queryKey: ["ai_conversations", conversationId] })
                }
                queryClient.invalidateQueries({ queryKey: ["ai_conversations"] })
                setTimeout(() => resetStreamContent(), 100)
                break

              case "RUN_ERROR": {
                setStreaming(false)
                resetStreamContent()
                const errorMsg = (d.message as string) || "Wystąpił błąd podczas przetwarzania."
                toast.error(errorMsg)
                // Invalidate so the persisted error message shows in chat history
                if (conversationId) {
                  queryClient.invalidateQueries({ queryKey: ["ai_conversations", conversationId] })
                }
                queryClient.invalidateQueries({ queryKey: ["ai_conversations"] })
                break
              }
            }
          }
        }
      } catch (err) {
        if ((err as Error).name !== "AbortError") {
          setStreaming(false)
          resetStreamContent()
          toast.error("Błąd połączenia z AI — spróbuj ponownie")
        }
      }
    },
    [
      setStreaming,
      appendStreamContent,
      resetStreamContent,
      addRichToolCall,
      appendToolArgs,
      completeRichToolCall,
      startStep,
      finishStep,
      setActiveConversation,
      queryClient,
    ]
  )

  const abort = useCallback(() => {
    abortRef.current?.abort()
    setStreaming(false)
  }, [setStreaming])

  return { startRun, abort }
}
